import { firebaseConfig } from "../firebase/firebase";
import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  updatePassword as firebaseUpdatePassword,
} from "firebase/auth";
import { userService } from "./userService";
import { ROLES, normalizeRole, canAssignRole } from "../utils/roles";

const getSecondaryAppInstance = () => {
  const appName = "userCreationApp";
  const existingApp = getApps().find((app) => app.name === appName);
  if (existingApp) return existingApp;
  return initializeApp(firebaseConfig, appName);
};

class AuthService {
  constructor() {
    this.auth = getAuth();
  }

  // ──────────────────────────────────────────────
  //  LOGIN / LOGOUT / PASSWORD
  // ──────────────────────────────────────────────

  async login(email, password) {
    if (!email || !password)
      throw new Error("Email and password are required.");
    try {
      const userCredential = await signInWithEmailAndPassword(
        this.auth,
        email,
        password
      );
      return await userService.buildUserProfile(userCredential.user);
    } catch (err) {
      console.error("[AuthService] Login failed:", err);
      throw err;
    }
  }

  async logout() {
    try {
      await firebaseSignOut(this.auth);
    } catch (err) {
      console.error("[AuthService] Logout failed:", err);
      throw err;
    }
  }

  async updatePassword(newPassword) {
    const user = this.auth.currentUser;
    if (!user) throw new Error("No authenticated user found.");
    if (!newPassword || newPassword.length < 6) {
      throw new Error("Password must be at least 6 characters.");
    }

    try {
      await firebaseUpdatePassword(user, newPassword);
    } catch (err) {
      if (err.code === "auth/requires-recent-login") {
        throw new Error(
          "Session expired. Please log out and log back in to change your password."
        );
      }
      throw err;
    }
  }

  // ──────────────────────────────────────────────
  //  SESSION MONITORING
  // ──────────────────────────────────────────────

  subscribeToAuthChanges(callback) {
    return onAuthStateChanged(this.auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const completeProfile = await userService.buildUserProfile(
            firebaseUser
          );
          callback(completeProfile);
        } catch (err) {
          console.error(
            "[AuthService] Profile hydration failed:",
            err
          );
          callback(
            userService.buildFallbackProfile(firebaseUser, {
              degraded: true,
            })
          );
        }
      } else {
        callback(null);
      }
    });
  }

  // ──────────────────────────────────────────────
  //  USER CREATION  (admin creates a new login)
  // ──────────────────────────────────────────────

  async createUserWithAuth(formData, currentUser, explicitRole = null) {
    if (!currentUser) throw new Error("Unauthenticated");

    const callerRole = normalizeRole(explicitRole || currentUser.role);

    // ── 1. Authorization check ──
    if (
      callerRole !== ROLES.SUPERADMIN &&
      callerRole !== ROLES.ADMIN &&
      callerRole !== ROLES.LOCATION_ADMIN
    ) {
      throw new Error(
        "Access Denied: Only administrators can create users with credentials."
      );
    }

    if (!formData?.email || !formData?.password) {
      throw new Error("Email and password parameters are mandatory.");
    }

    // ── 2. Role assignment check ──
    const requestedRole = normalizeRole(formData.role || ROLES.USER);
    if (!canAssignRole(callerRole, requestedRole)) {
      throw new Error(
        `Role "${requestedRole}" is not assignable by your account.`
      );
    }

    // ── 3. Company assignment (FK only — no name lookup) ──
    const assignedCompanyId =
      callerRole === ROLES.SUPERADMIN
        ? formData.companyId
        : currentUser.companyId;

    if (!assignedCompanyId || String(assignedCompanyId).trim() === "") {
      throw new Error("Company assignment is required.");
    }

      // ── 4. Location validation (FK only — no name lookup) ──
    let assignedLocationCode = null;

    if (callerRole === ROLES.LOCATION_ADMIN) {
      // Location Admin MUST have a location context to create users
      assignedLocationCode =
        currentUser.locationCode || currentUser.locationId || null;

      if (
        !assignedLocationCode ||
        String(assignedLocationCode).trim() === ""
      ) {
        throw new Error(
          "Access Denied: Location context missing for Location Admin. Cannot create users."
        );
      }
    } else if (callerRole !== ROLES.SUPERADMIN) {
      // Admins require a location assignment, Superadmins do not.
      assignedLocationCode = formData.locationCode || null;

      if (
        !assignedLocationCode ||
        String(assignedLocationCode).trim() === ""
      ) {
        throw new Error(
          "Location is required. Every user must be assigned a location."
        );
      }
    }

    // Only trim if a value was actually assigned
    if (assignedLocationCode) {
      assignedLocationCode = String(assignedLocationCode).trim();
    }

    // ── 5. Auth & Firestore creation ──
    const secondaryApp = getSecondaryAppInstance();
    const secondaryAuth = getAuth(secondaryApp);
    let authUser = null;

    try {
      const credential = await createUserWithEmailAndPassword(
        secondaryAuth,
        formData.email,
        formData.password
      );
      authUser = credential.user;

      // Only include keys the model recognizes. UserModel fills in
      // createdAt / modifiedAt with `now` when they're missing.
      const rawPayload = {
        id: authUser.uid,
        email: formData.email,
        name: formData.name || formData.displayName || "",
        role: requestedRole,
        companyId: assignedCompanyId,
        departmentId: formData.departmentId || null,
        locationCode: assignedLocationCode,
        description: formData.description || null,
        status: "active",
        metadata: {
          createdBy: currentUser.email || "system",
          modifiedBy: currentUser.email || "system",
        },
      };

      const saved = await userService.createUser(rawPayload, {
        ...currentUser,
        role: callerRole,
        createdByRole: callerRole,
      });

      return saved;
    } catch (err) {
      console.error("[AuthService] User creation failed:", err);

      if (authUser) {
        try {
          await authUser.delete();
          console.warn(
            "[AuthService] Cleaned up orphaned Auth account."
          );
        } catch (cleanupErr) {
          console.error(
            "[AuthService] Orphan cleanup failed:",
            cleanupErr
          );
        }
      }
      throw err;
    } finally {
      try {
        if (secondaryAuth.currentUser) {
          await firebaseSignOut(secondaryAuth);
        }
      } catch (signOutErr) {
        console.error(
          "[AuthService] Secondary Auth sign-out failed:",
          signOutErr
        );
      }
    }
  }
}

export const authService = new AuthService();